<script lang="ts">
    import type {DocumentDetailReactivePageData, DocumentDetailStaticPageData} from '../../../../record/kp-document-detail-page/types';
    import type {RecordHolding, User} from 'typings/portaro.be.types';
    import type {UserDetailView} from 'src/features/user/types';
    import {getLocalization} from 'core/svelte-context/context';
    import {getPageContext} from 'shared/layouts/page-context';
    import {onDestroy} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {UserRoles} from 'shared/services/current-auth.service';
    import KpGenericTopbar from 'shared/components/kp-generic-topbar/KpGenericTopbar.svelte';
    import KpVerticalSeparator from 'shared/ui-widgets/separator/KpVerticalSeparator.svelte';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';
    import GenericRecordDetailSettingsPopover from './GenericRecordDetailSettingsPopover.svelte';
    import KpUserAvatar from 'shared/components/kp-user-avatar/KpUserAvatar.svelte';
    import Spacer from 'shared/layouts/flex/Spacer.svelte';
    import {SutorFondChip} from 'src/features/erp-sutor/shared';

    export let user: User | null = null;
    export let userDetailView: UserDetailView | null = null;

    const localize = getLocalization();
    const pageContext = getPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>();
    const model = pageContext.staticData.model;

    let recordHoldings: RecordHolding[] = [];
    const reactiveDataUnsubscribe = pageContext.reactiveData.subscribe((data) => recordHoldings = data.visibleRecordHoldings ?? []);
    onDestroy(reactiveDataUnsubscribe);
</script>

<KpGenericTopbar height="var(--sutor-topbar-height)" gap="24px">
    <div class="title-container">
        <SutorFondChip chipSize="sm" fondId={model.record.fond.id} fondName={model.record.fond.text}>{model.record.fond.text}</SutorFondChip>

        {#if exists(user)}
            {#if exists(user.deletionEventId)}
                <KpChipTag chipStyle="danger" dataQa="reader-role-label-deleted">
                    {localize(/* @kp-localization commons.Deleted */ 'commons.Deleted')}
                </KpChipTag>
            {/if}

            {#if user.readerAccounts[0]?.blocked}
                <KpChipTag chipStyle="danger" dataQa="reader-role-label-blocked">
                    {localize(/* @kp-localization ctenar.TransactionsBlocked */ 'ctenar.TransactionsBlocked')}
                </KpChipTag>
            {/if}

            {#if user.readerAccounts[0]?.registrationExpired}
                <KpChipTag chipStyle="danger" dataQa="reader-role-label-registration-expired">
                    {localize(/* @kp-localization ctenar.RegistrationExpired */ 'ctenar.RegistrationExpired')}
                </KpChipTag>
            {/if}

            {#if user.role.includes(UserRoles.ROLE_READER)}
                <KpChipTag chipStyle="primary" dataQa="reader-role-label-reader">
                    {localize(/* @kp-localization user.Reader */ 'user.Reader')}
                </KpChipTag>
            {/if}

            {#if user.role.includes(UserRoles.ROLE_LIBRARIAN)}
                <KpChipTag chipStyle="primary" dataQa="reader-role-label-librarian">
                    {localize(/* @kp-localization user.Librarian */ 'user.Librarian')}
                </KpChipTag>
            {/if}

            {#if user.role.includes(UserRoles.ROLE_LIBRARY)}
                <KpChipTag chipStyle="primary" dataQa="reader-role-label-library">
                    {localize(/* @kp-localization user.MvsLibrary */ 'user.MvsLibrary')}
                </KpChipTag>
            {/if}

            {#if user.role.includes(UserRoles.ROLE_SUPPLIER)}
                <KpChipTag chipStyle="primary" dataQa="reader-role-label-supplier">
                    {localize(/* @kp-localization user.Supplier */ 'user.Supplier')}
                </KpChipTag>
            {/if}

            <KpUserAvatar userRecordId={user.rid} sizePx={24}/>
        {/if}

        <span class="record-name">
            {#if !exists($$slots['record-name'])}
                {model.record.name}
            {:else}
                <slot name="record-name" record={model.record}/>
            {/if}
        </span>
    </div>

    {#if recordHoldings.length > 0}
        <KpVerticalSeparator height="20px"/>

        <div class="holdings-container">
            {#each recordHoldings as recordHolding(recordHolding.id)}
                <KpChipTag chipSize="xs">
                    {pipe(recordHolding.department, loc())}
                </KpChipTag>
            {/each}
        </div>
    {/if}

    <Spacer flex="1"/>

    <GenericRecordDetailSettingsPopover {user} {userDetailView}/>
</KpGenericTopbar>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .title-container {
        display: flex;
        align-items: center;
        gap: @spacing-m;

        .record-name {
            white-space: nowrap;
        }
    }

    .holdings-container {
        display: flex;
        align-items: center;
        gap: @spacing-s;
        overflow-x: auto;
        height: 100%;
    }
</style>